/**
 * Bot Monitoring Hook
 * Provides 5-second polling for bot status and offers/loans data
 */
import { useQuery } from '@tanstack/react-query'
import {
  getBotStatus,
  getBotOffers,
  getBotLoans,
  getEarningsChart,
} from '../services/api/botMonitoring'

/**
 * Hook to fetch and auto-refresh bot status every 5 seconds
 */
export function useBotStatus(botId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['bot-status', botId],
    queryFn: () => getBotStatus(botId),
    enabled,
    refetchInterval: 5000, // 5 seconds
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider stale
  })
}

/**
 * Hook to fetch and auto-refresh bot offers every 5 seconds
 */
export function useBotOffers(botId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['bot-offers', botId],
    queryFn: () => getBotOffers(botId),
    enabled,
    refetchInterval: 5000, // 5 seconds
    refetchIntervalInBackground: true,
    staleTime: 0,
  })
}

/**
 * Hook to fetch and auto-refresh bot loans every 5 seconds
 */
export function useBotLoans(botId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['bot-loans', botId],
    queryFn: () => getBotLoans(botId),
    enabled,
    refetchInterval: 5000, // 5 seconds
    refetchIntervalInBackground: true,
    staleTime: 0,
  })
}

/**
 * Hook to fetch bot earnings chart (no polling, manual refresh)
 */
export function useBotEarningsChart(botId: number, days: number = 7, enabled: boolean = true) {
  return useQuery({
    queryKey: ['bot-earnings-chart', botId, days],
    queryFn: () => getEarningsChart(botId, days),
    enabled,
    staleTime: 60000, // Cache for 1 minute
  })
}
